/**
 * Matemática pura de ángulos y distancias entre keypoints.
 *
 * Todas las funciones son puras — sin estado, sin side effects.
 * Se pueden testear sin cámara con keypoints mock.
 */

import type { Keypoint } from "./types";

/** Longitud de un vector 3D. */
function magnitude3D(x: number, y: number, z: number): number {
  return Math.sqrt(x * x + y * y + z * z);
}

/** Longitud de un vector 2D. */
function magnitude2D(x: number, y: number): number {
  return Math.sqrt(x * x + y * y);
}

/**
 * Ángulo 3D en el vértice `b`, formado por los puntos a-b-c.
 * Devuelve grados en [0, 180].
 *
 * Usa la coordenada z de MediaPipe (estimación de profundidad).
 * Más preciso que calc2DAngle para articulaciones con rotación fuera del plano
 * (ej: rotación externa del hombro).
 */
export function calc3DAngle(a: Keypoint, b: Keypoint, c: Keypoint): number {
  // Vectores desde el vértice b hacia los otros dos puntos
  const v1x = a.x - b.x;
  const v1y = a.y - b.y;
  const v1z = a.z - b.z;

  const v2x = c.x - b.x;
  const v2y = c.y - b.y;
  const v2z = c.z - b.z;

  const dot = v1x * v2x + v1y * v2y + v1z * v2z;
  const m1 = magnitude3D(v1x, v1y, v1z);
  const m2 = magnitude3D(v2x, v2y, v2z);

  if (m1 === 0 || m2 === 0) return 0;

  // Clamp por errores de precisión flotante
  const cos = Math.max(-1, Math.min(1, dot / (m1 * m2)));
  return (Math.acos(cos) * 180) / Math.PI;
}

/**
 * Ángulo 2D en el vértice `b`, formado por los puntos a-b-c.
 * Ignora la coordenada z. Útil cuando queremos medir un ángulo
 * proyectado al plano de la cámara.
 */
export function calc2DAngle(a: Keypoint, b: Keypoint, c: Keypoint): number {
  const v1x = a.x - b.x;
  const v1y = a.y - b.y;
  const v2x = c.x - b.x;
  const v2y = c.y - b.y;

  const dot = v1x * v2x + v1y * v2y;
  const m1 = magnitude2D(v1x, v1y);
  const m2 = magnitude2D(v2x, v2y);

  if (m1 === 0 || m2 === 0) return 0;

  const cos = Math.max(-1, Math.min(1, dot / (m1 * m2)));
  return (Math.acos(cos) * 180) / Math.PI;
}

/** Distancia 3D entre dos keypoints (en unidades normalizadas [0..1]). */
export function distance3D(a: Keypoint, b: Keypoint): number {
  return magnitude3D(a.x - b.x, a.y - b.y, a.z - b.z);
}

/** Distancia 2D entre dos keypoints. */
export function distance2D(a: Keypoint, b: Keypoint): number {
  return magnitude2D(a.x - b.x, a.y - b.y);
}

/** Distancia horizontal (solo eje X) — útil para detectar "codo separado del cuerpo". */
export function horizontalDistance(a: Keypoint, b: Keypoint): number {
  return Math.abs(a.x - b.x);
}

/**
 * Ángulo del torso respecto de la vertical del frame.
 * 0° = torso perfectamente vertical. Valores positivos = inclinado a la derecha.
 * Útil para detectar compensación con el tronco durante un ejercicio de brazo.
 */
export function trunkAngle(
  leftShoulder: Keypoint,
  rightShoulder: Keypoint,
  leftHip: Keypoint,
  rightHip: Keypoint,
): number {
  const shoulderMidX = (leftShoulder.x + rightShoulder.x) / 2;
  const shoulderMidY = (leftShoulder.y + rightShoulder.y) / 2;
  const hipMidX = (leftHip.x + rightHip.x) / 2;
  const hipMidY = (leftHip.y + rightHip.y) / 2;

  const dx = shoulderMidX - hipMidX;
  const dy = shoulderMidY - hipMidY;

  // atan2 devuelve el ángulo del vector (dx, dy) respecto del eje positivo x.
  // Queremos el ángulo respecto de la vertical (y apunta hacia abajo en coords de imagen).
  // dy será negativo si los hombros están arriba de las caderas (normal).
  return (Math.atan2(dx, -dy) * 180) / Math.PI;
}

/** Promedio de visibility de una lista de keypoints. Si baja de 0.5, el frame no es confiable. */
export function avgVisibility(keypoints: Keypoint[], indices: number[]): number {
  if (indices.length === 0) return 0;
  let sum = 0;
  for (const i of indices) {
    sum += keypoints[i]?.visibility ?? 0;
  }
  return sum / indices.length;
}
